import classNames from "classnames";
import React, { AriaAttributes, Component, createRef, RefObject } from "react";
import { IconRadioChecked, IconRadioCheckedDisabled, IconRadioUnchecked, IconRadioUncheckedDisabled } from "../icons";
import { Text } from "../text/text";
import { ColorOption, InputElementAttributes, LabelPlacementOption, WidthOption } from "../types";
import { CSS_CLASSES } from "./radio-css-classes";

let nextUniqueId = 0;

const getNextUniqueId = () => `fbds-radio-${++nextUniqueId}`;

export type RadioAriaAttributes = Partial<AriaAttributes>;

export type RadioColorOption = Exclude<ColorOption, "white" | "black">;

export interface RadioIcons {
  checked: JSX.Element;
  unchecked: JSX.Element;
  checkedDisabled: JSX.Element;
  uncheckedDisabled: JSX.Element;
}

export interface RadioProps
  extends Omit<
    InputElementAttributes,
    "min" | "max" | "minLength" | "maxLength" | "step" | "type" | "autoComplete" | "inputMode" | "placeholder"
  > {
  /**
   * Specify the state of the radio - checked or not.
   */
  checked: boolean;

  /**
   * The label to be associated with the `<input>` element.
   *
   * Required from an A11y perspective.
   */
  label: string;

  /**
   * If true, renders the label as a visual label element.
   *
   * If false, renders an aria-label attribute on the input element instead.
   *
   * @default false
   */
  hideLabel?: boolean;

  /**
   * Specify the label placement, if label visible.
   *
   * @default right
   */
  labelPlacement?: LabelPlacementOption;

  /**
   * Specify the width of the radio
   * Width will be responsible for rendering radio to different widths supported
   * auto: will render radio as per the length of the label and increase with as label gets bigger
   * full: will render the radio occupying full available width
   *
   * @default auto
   */
  width?: WidthOption;

  /**
   * Specify the color of the radio icon
   *
   * @default interactive
   */
  color?: RadioColorOption;

  /**
   * Custom Icons to use to render the visual radio control
   */
  icons?: RadioIcons;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * Prop to pass a `ref` to the underlying HTML `<input> element.`
   */
  inputRef?: RefObject<HTMLInputElement>;
}

/**
 * The Radio component MUST be used any time a “radio” user input is needed.
 *
 * It MUST allow the user of this component to render the component with various
 * state specific styles defined in the Blueprint Design System.
 *
 * This component is implemented to be used as a controlled form component.
 */
export class Radio extends Component<RadioProps> {
  static defaultProps = {
    width: "auto",
    color: "interactive",
    labelPlacement: "right",
    icons: {
      checked: <IconRadioChecked aria-hidden="true" />,
      unchecked: <IconRadioUnchecked aria-hidden="true" />,
      checkedDisabled: <IconRadioCheckedDisabled aria-hidden="true" />,
      uncheckedDisabled: <IconRadioUncheckedDisabled aria-hidden="true" />,
    },
  } as RadioProps;

  inputRef = this.props.inputRef || createRef<HTMLInputElement>();

  radioId: string;
  labelId: string;

  constructor(props: RadioProps) {
    super(props);

    this.radioId = this.props.id || getNextUniqueId();
    this.labelId = `${this.radioId}-label`;
  }

  render() {
    const {
      /* Input Base props */
      name,
      value,
      required,
      disabled,
      readOnly,
      onChange,
      onFocus,
      onBlur,
      inputRef,
      /* Own props */
      checked,
      label,
      hideLabel,
      labelPlacement,
      width,
      color,
      icons,
      className,
      /* Rest props */
      ...restProps
    } = this.props;

    const radioRootClassName = classNames(
      CSS_CLASSES.RADIO_ROOT,
      {
        [CSS_CLASSES.RADIO_ROOT_FULL]: width === "full",
        [CSS_CLASSES.RADIO_ROOT_AUTO]: width === "auto",

        [CSS_CLASSES.RADIO_ROOT_AUTO_TOP]: width === "auto" && labelPlacement === "top",
        [CSS_CLASSES.RADIO_ROOT_AUTO_RIGHT]: width === "auto" && labelPlacement === "right",
        [CSS_CLASSES.RADIO_ROOT_AUTO_BOTTOM]: width === "auto" && labelPlacement === "bottom",
        [CSS_CLASSES.RADIO_ROOT_AUTO_LEFT]: width === "auto" && labelPlacement === "left",

        [CSS_CLASSES.RADIO_ROOT_FULL_TOP]: width === "full" && labelPlacement === "top",
        [CSS_CLASSES.RADIO_ROOT_FULL_RIGHT]: width === "full" && labelPlacement === "right",
        [CSS_CLASSES.RADIO_ROOT_FULL_BOTTOM]: width === "full" && labelPlacement === "bottom",
        [CSS_CLASSES.RADIO_ROOT_FULL_LEFT]: width === "full" && labelPlacement === "left",

        [CSS_CLASSES.RADIO_ROOT_DISABLED]: disabled,
      },
      className
    );

    const radioIconClassName = classNames(CSS_CLASSES.RADIO_ICON, {
      [CSS_CLASSES.RADIO_ICON_CHECKED_PRIMARY]: checked && !disabled && color === "primary",
      [CSS_CLASSES.RADIO_ICON_CHECKED_SECONDARY]: checked && !disabled && color === "secondary",
      [CSS_CLASSES.RADIO_ICON_CHECKED_INTERACTIVE]: checked && !disabled && color === "interactive",
      [CSS_CLASSES.RADIO_ICON_CHECKED_ACCENT]: checked && !disabled && color === "accent",
      [CSS_CLASSES.RADIO_ICON_CHECKED_GRAY]: checked && !disabled && color === "gray",
      [CSS_CLASSES.RADIO_ICON_CHECKED_ERROR]: checked && !disabled && color === "error",
      [CSS_CLASSES.RADIO_ICON_CHECKED_WARNING]: checked && !disabled && color === "warning",
      [CSS_CLASSES.RADIO_ICON_CHECKED_SUCCESS]: checked && !disabled && color === "success",

      [CSS_CLASSES.RADIO_ICON_UNCHECKED]: !checked && !disabled,

      [CSS_CLASSES.RADIO_ICON_DISABLED]: disabled,
    });

    const radioLabelContainerClassName = classNames(CSS_CLASSES.RADIO_LABEL_CONTAINER, {
      [CSS_CLASSES.RADIO_LABEL_TOP]: labelPlacement === "top",
      [CSS_CLASSES.RADIO_LABEL_RIGHT]: labelPlacement === "right",
      [CSS_CLASSES.RADIO_LABEL_BOTTOM]: labelPlacement === "bottom",
      [CSS_CLASSES.RADIO_LABEL_LEFT]: labelPlacement === "left",
    });

    const ariaAttributes: RadioAriaAttributes = {};
    if (hideLabel) {
      ariaAttributes["aria-label"] = label;
    } else {
      ariaAttributes["aria-labelledby"] = this.labelId;
    }

    return (
      <label htmlFor={this.radioId} id={this.labelId} className={radioRootClassName}>
        <input
          ref={this.inputRef}
          type="radio"
          className={CSS_CLASSES.RADIO_INPUT_ELEMENT}
          id={this.radioId}
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          {...ariaAttributes}
          required={required}
          disabled={disabled}
          {...restProps}
        />
        <span className={radioIconClassName}>
          <div aria-hidden="true" className={CSS_CLASSES.RADIO_ICON_INNER_CIRCLE} />
          {disabled
            ? checked
              ? icons.checkedDisabled
              : icons.uncheckedDisabled
            : checked
            ? icons.checked
            : icons.unchecked}
        </span>
        {!hideLabel && (
          <span className={radioLabelContainerClassName}>
            {required ? (
              <Text className={CSS_CLASSES.RADIO_LABEL_REQUIRED_INDICATOR} variant="body-long">
                *
              </Text>
            ) : null}
            <Text className={CSS_CLASSES.RADIO_LABEL_TEXT} variant="body-long">
              {label}
            </Text>
          </span>
        )}
      </label>
    );
  }
}
